import { useEffect, useState } from "react";
import { OnboardingStep } from "../onboarding-components/onboarding-step";
import { ONBOARDING_STATUS, ONBOARDING_STEPS } from "@/dentlab/src/constants/onboarding";
import { Region } from "@/dentlab/src/types/enums";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { PriceList, PriceListImport } from "@/dentlab/src/types/price-list.types";
import { useAttributesStore } from "@/dentlab/src/store/Attributes";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/dentlab/src/components/ui/table";
import { Star } from "lucide-react";
import { StarOff } from "lucide-react";
import { Input } from "@/dentlab/src/components/ui/input";
import { Loader2, FileInput } from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import { PriceListMockup } from "../onboarding-components/onboarding-mockups/PriceListMockup";
import { StepProps } from "./types";

interface PriceListValidationParams {
    region: Region;
    selectedDefaultPriceListId: string | null;
}

const validatePriceList = (params: PriceListValidationParams): string | null => {
    if (params.region === Region.CH && !params.selectedDefaultPriceListId) {
        return "Bitte wählen Sie eine Standard-Preisliste aus";
    }
    return null;
};

interface StepPriceListsProps extends StepProps {
    region: Region;
}

export const StepPriceLists: React.FC<StepPriceListsProps> = ({ onComplete, stepNumber, totalSteps, canGoBack, onGoBack, region }) => {
    const {
        organization,
        priceLists,
        updatePriceList,
        updateOrganizationDefault,
        priceListImports,
        priceListImportsLoading,
        loadPriceListImports,
        importPriceListItems,
    } = useCentralStore((state) => ({
        organization: state.organization,
        priceLists: state.priceLists,
        updatePriceList: state.updatePriceList,
        updateOrganizationDefault: state.updateOrganizationDefault,
        priceListImports: state.priceListImports,
        priceListImportsLoading: state.priceListImportsLoading,
        loadPriceListImports: state.loadPriceListImports,
        importPriceListItems: state.importPriceListItems,
    }));
    const { setOnboardingAttribute } = useAttributesStore((state) => ({
        setOnboardingAttribute: state.setOnboardingAttribute,
    }));

    const [importingId, setImportingId] = useState<string | null>(null);
    const [localPriceLists, setLocalPriceLists] = useState<PriceList[]>([]);
    const [selectedDefaultPriceListId, setSelectedDefaultPriceListId] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        // Initialize local price lists from store
        if (localPriceLists.length === 0) {
            setLocalPriceLists([...priceLists]);
        }

        // Initialize selected default from organization
        if (!selectedDefaultPriceListId && organization?.default_price_list_id) {
            setSelectedDefaultPriceListId(organization.default_price_list_id);
        } else if (!selectedDefaultPriceListId && region === Region.CH) {
            // For CH: Set pp2 as default if exists and no default is set
            const pp2 = priceLists.find(p => p.name.toLowerCase() === 'pp2');
            if (pp2) {
                setSelectedDefaultPriceListId(pp2.id);
            }
        }
        // For DE: Load price list imports
        if (region === Region.DE && priceListImports.length === 0) {
            loadPriceListImports();
        }
    }, []);

    const handleSubmit = async () => {
        const error = validatePriceList({ region, selectedDefaultPriceListId });
        if (error) {
            setErrorMessage(error);
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        try {
            // Commit all local price list changes to store/database
            for (const localPriceList of localPriceLists) {
                const originalPriceList = priceLists.find(pl => pl.id === localPriceList.id);
                if (originalPriceList && localPriceList.tp_value !== originalPriceList.tp_value) {
                    await updatePriceList(localPriceList.id, 'tp_value', localPriceList.tp_value);
                }
            }

            // Update default price list if changed
            if (selectedDefaultPriceListId && selectedDefaultPriceListId !== organization?.default_price_list_id) {
                await updateOrganizationDefault("default_price_list_id", selectedDefaultPriceListId);
            }

            await setOnboardingAttribute(ONBOARDING_STEPS.PRICE_LIST, ONBOARDING_STATUS.COMPLETED, 'org');
            onComplete();
        } catch (error) {
            setErrorMessage("Fehler beim Speichern der Preisliste. Bitte versuchen Sie es erneut.");
        } finally {
            setLoading(false);
        }
    }

    const handlePriceListFieldUpdate = (id: string, field: keyof PriceList, value: any) => {
        setLocalPriceLists(prev => prev.map(pl =>
            pl.id === id ? { ...pl, [field]: value } : pl
        ));
    };

    const handleSetDefaultPriceList = (id: string) => {
        setSelectedDefaultPriceListId(id);
    };

    const handleImportPriceList = async (importItem: PriceListImport) => {
        setImportingId(importItem.id);
        const result = await importPriceListItems(importItem.id);
        if (result && result.price_list_id) {
            // Set as default after import
            await updateOrganizationDefault("default_price_list_id", result.price_list_id);
        }
        setImportingId(null);
    };

    return (
        <OnboardingStep
            stepTitle="Preisliste"
            stepDescription="Wählen Sie Ihre Standard-Preisliste und den dazugehörigen TP-Wert für neue Aufträge. Nach dem Onboarding können Sie weitere Preislisten anlegen und anpassen."
            onButtonClick={handleSubmit}
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            canGoBack={canGoBack}
            onGoBackClick={onGoBack}
            buttonText="Weiter"
            buttonDisabled={false}
            loading={loading}
            errorMessage={errorMessage}
            onErrorMessageClose={() => setErrorMessage(null)}
            rightPanel={<PriceListMockup />}
        >
            <div className="space-y-4">
                {region == Region.CH ? (
                    // Swiss (CH) version: Show simplified price list table
                    <>
                        {localPriceLists.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                Keine Preislisten vorhanden.
                            </div>
                        ) : (
                            <div className="border rounded-lg">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-8"></TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead className="w-32">TP-Wert</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {localPriceLists.map((priceList) => (
                                            <TableRow key={priceList.id}>
                                                <TableCell>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetDefaultPriceList(priceList.id)}
                                                        className="p-1 hover:bg-gray-100 rounded"
                                                        title={
                                                            selectedDefaultPriceListId === priceList.id
                                                                ? "Als Standard entfernen"
                                                                : "Als Standard setzen"
                                                        }
                                                    >
                                                        {selectedDefaultPriceListId === priceList.id ? (
                                                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                                                        ) : (
                                                            <StarOff className="h-4 w-4 text-gray-400" />
                                                        )}
                                                    </button>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm font-medium">{priceList.name}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        value={priceList.tp_value}
                                                        onChange={(e) => handlePriceListFieldUpdate(priceList.id, 'tp_value', e.target.value)}
                                                        className="h-10"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </>
                ) : (
                    // German (DE) version: Show import options
                    <>
                        {priceListImportsLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                            </div>
                        ) : priceListImports.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                Keine Import-Vorlagen verfügbar. Legen Sie erst eine Region fest (vorheriger Schritt).
                            </div>
                        ) : (
                            <div className="border rounded-lg">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead className="w-32"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {priceListImports.map((importItem) => (
                                            <TableRow key={importItem.id}>
                                                <TableCell className="font-medium">
                                                    {importItem.name}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        onClick={() => handleImportPriceList(importItem)}
                                                        disabled={importingId === importItem.id}
                                                        size="sm"
                                                    >
                                                        {importingId === importItem.id ? (
                                                            <>
                                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                                Importiere...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FileInput className="h-4 w-4 mr-2" />
                                                                Importieren
                                                            </>
                                                        )}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </OnboardingStep>
    )
}